import React, { useState, useEffect } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  Avatar,
  Stack,
} from "@mui/material";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import { getUser } from "../../api/users";
import useAuth from "../../hooks/useAuth";
import { APP_HOME_PATH } from "../../config/routes";

const UserProfile = () => {
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const fetchUserData = async () => {
      try {
        setLoading(true);

        setLoading(true);
        setError(null);

        const response = await getUser(id);

        // Authorize AFTER we know the target user's tenant
        const isTenantAdmin = currentUser?.roles?.some(
          (r) => r === "TenantAdmin" || r?.name === "TenantAdmin"
        );
        if (isTenantAdmin && currentUser?.tenant_id !== response?.tenant_id) {
          throw new Error("Unauthorized access");
        }

        if (!cancelled) setUserData(response);
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load user data");
        console.error("Error fetching user:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchUserData();
    return () => {
      cancelled = true;
    };
  }, [id, currentUser]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!userData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>User not found</Typography>
      </Box>
    );
  }

  const isSuperAdmin = currentUser?.roles?.some(
    (r) => r === "super_admin" || r?.name === "super_admin"
  );
  const isTenantAdmin = currentUser?.roles?.some(
    (r) => r === "TenantAdmin" || r?.name === "TenantAdmin"
  );

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 2 }} aria-label="breadcrumb">
        <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
          Dashboard
        </Link>
        <Link
          component={RouterLink}
          underline="hover"
          color="inherit"
          to="/admin/users"
        >
          Users
        </Link>
        <Typography color="text.primary">
          {userData.firstname} {userData.lastname}
        </Typography>
      </Breadcrumbs>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Typography variant="h4">User profile</Typography>
        {(isSuperAdmin ||
          (isTenantAdmin &&
            currentUser.tenant_id === userData.tenant_id)) && (
          <Button
            variant="contained"
            onClick={() => navigate(`/admin/users/${id}/edit`)}
            sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}
          >
            Edit Profile
          </Button>
        )}
      </Stack>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "center", sm: "flex-start" }}
          sx={{ mb: 3, textAlign: { xs: "center", sm: "left" } }}
        >
          <Avatar
            sx={{
              width: 100,
              height: 100,
              fontSize: 40,
            }}
          >
            {userData.firstname?.[0]}
            {userData.lastname?.[0]}
          </Avatar>
          <Box>
            <Typography variant="h4">
              {userData.firstname} {userData.lastname}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {userData.tenant_name || "No tenant assigned"}
            </Typography>
            <Chip
              label={userData.is_active ? "Active" : "Inactive"}
              color={userData.is_active ? "success" : "error"}
              sx={{ mt: 1 }}
            />
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <List>
          <ListItem>
            <ListItemText primary="Username" secondary={userData.username} />
          </ListItem>
          <Divider component="li" />
          <ListItem>
            <ListItemText primary="Email" secondary={userData.email} />
          </ListItem>
          <Divider component="li" />
          <ListItem>
            <ListItemText
              primary="Tenant"
              secondary={userData.tenant_name || "No tenant assigned"}
            />
          </ListItem>
          <Divider component="li" />
          <ListItem>
            <ListItemText
              primary="Account Status"
              secondary={
                <Chip
                  label={userData.is_active ? "Active" : "Inactive"}
                  color={userData.is_active ? "success" : "error"}
                />
              }
              secondaryTypographyProps={{ component: "div" }}
            />
          </ListItem>
          <Divider component="li" />
          <ListItem>
            <ListItemText
              primary="Roles"
              secondary={
                userData.roles?.length > 0 ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {userData.roles.map((role) => (
                      <Chip
                        key={role.id}
                        label={role.name}
                        color="primary"
                        size="small"
                      />
                    ))}
                  </Box>
                ) : (
                  "No roles assigned"
                )
              }
              secondaryTypographyProps={{ component: "div" }}
            />
          </ListItem>
        </List>
      </Paper>

      <Button
        variant="outlined"
        component={RouterLink}
        to="/admin/users"
        sx={{ mt: 2 }}
      >
        Back to users
      </Button>
    </Box>
  );
};

export default UserProfile;
